/**
 * Turning the flat `group-all-payment/init` list into something a person can act on.
 *
 * The endpoint already tags every payable with a `type` — `dins` (installment
 * past its due date), `ins` (installment coming up), `psp` (حق عضویت) and
 * `msp` (cashbox share). The PWA used to throw that away and render one
 * undifferentiated list of description + amount + checkbox, which is why a
 * member with an installment 56 days late saw it sandwiched between two things
 * that are not due yet.
 *
 * Everything below is derived from the payload, never from a second request.
 */

export type PayableBucket =
    | 'overdue' // اقساط سررسید شده — the only thing that is actually late
    | 'dues' // حق عضویت
    | 'upcoming' // اقساط پیش‌رو
    | 'cashbox' // سهم قرعه‌کشی
    | 'record' // صندوق ثبت‌دستی — visible, never payable through us

export interface Payable extends IGetAllPayment {
    /**
     * `id` alone is NOT unique across the list: it is a Poolship pk for `psp`,
     * an Installment pk for `ins`/`dins` and a Membership pk for `msp`. Keying
     * or comparing on it made Poolship 500 and Installment 500 toggle together
     * and gave React duplicate keys on the same list.
     */
    key: string
    bucket: PayableBucket
    isSelected: boolean
}

export const BUCKET_LABELS: Record<PayableBucket, string> = {
    overdue: 'اقساط سررسید شده',
    dues: 'حق عضویت',
    upcoming: 'اقساط پیش‌رو',
    cashbox: 'سهم قرعه‌کشی',
    record: 'پرداخت نزد مدیر صندوق',
}

/** The order groups appear in, most urgent first. */
export const BUCKET_ORDER: PayableBucket[] = [
    'overdue',
    'dues',
    'upcoming',
    'cashbox',
    'record',
]

export const payableKey = (item: IGetAllPayment) =>
    `${item.type}-${item.box_id}-${item.id}`

/**
 * MR 1 adds `is_overdue` / `is_payable` to the payload. This reads them
 * defensively so the screen behaves correctly against a backend that has not
 * shipped them yet — `dins` has always meant "past due", and a box that
 * reached the client at all used to be payable by definition.
 */
export const isOverdue = (item: IGetAllPayment) =>
    item.is_overdue ?? item.type === 'dins'

export const isPayable = (item: IGetAllPayment) => item.is_payable ?? true

/**
 * Whole days until this falls due, or undefined when we cannot tell.
 *
 * The backend already returns everything due within the next 30 days, so
 * upcoming installments are in the payload — they just had nothing on them
 * saying when they land. Derived from `due_date` rather than asking for
 * another field, and undefined on a backend that does not send it (in which
 * case the row simply shows no badge, which is what it did before).
 */
export const daysUntilDue = (item: IGetAllPayment): number | undefined => {
    if (!item.due_date) return undefined
    const due = new Date(`${item.due_date}T00:00:00`)
    if (Number.isNaN(due.getTime())) return undefined
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return Math.round((due.getTime() - today.getTime()) / 86_400_000)
}

export const bucketOf = (item: IGetAllPayment): PayableBucket => {
    if (!isPayable(item)) return 'record'
    if (item.type === 'psp') return 'dues'
    if (item.type === 'msp') return 'cashbox'
    return isOverdue(item) ? 'overdue' : 'upcoming'
}

/**
 * Which buckets a given entry point starts with ticked.
 *
 * The default is overdue-only. Ticking everything (which is what this screen
 * used to do) means a member with 900k overdue and 600k not yet due opens the
 * screen pre-committed to 1.5M they never chose.
 */
export type PreselectMode = 'overdue' | 'dues' | 'all'

const PRESELECTED: Record<PreselectMode, PayableBucket[]> = {
    overdue: ['overdue'],
    // The home card's second row is «حق عضویت و پرداختنی‌های پیش‌رو», so its
    // CTA must arrive with exactly that set ticked.
    dues: ['dues', 'upcoming', 'cashbox'],
    all: ['overdue', 'dues', 'upcoming', 'cashbox'],
}

export const toPayables = (
    data: IGetAllPayment[] | undefined,
    preselect: PreselectMode
): Payable[] => {
    if (!data) return []
    const wanted = PRESELECTED[preselect]
    return data.map((item) => {
        const bucket = bucketOf(item)
        return {
            ...item,
            key: payableKey(item),
            bucket,
            // A record-mode item can never be selected: there is nothing to
            // send it to.
            isSelected: bucket !== 'record' && wanted.includes(bucket),
        }
    })
}

export interface RecordFund {
    boxKey: string
    boxName: string
    /** amount + commission across every record row of this fund */
    total: number
    info?: IGetAllPayment['manual_payment']
}

/**
 * Record-mode rows collapsed to one entry per FUND.
 *
 * The card number and the manager belong to the fund, not to each installment
 * — printing them under five rows of the same fund reads as five different
 * instructions about where to send the money.
 *
 * `boxKey` includes the box type: a cashbox and a moneypool can share an id.
 */
export const recordModeFunds = (payables: Payable[]): RecordFund[] => {
    const byBox = new Map<string, RecordFund>()
    payables
        .filter((p) => p.bucket === 'record')
        .forEach((p) => {
            const boxKey = `${p.box_type ?? 'mnp'}-${p.box_id}`
            const existing = byBox.get(boxKey)
            if (existing) {
                existing.total += p.amount + p.commission
                return
            }
            byBox.set(boxKey, {
                boxKey,
                boxName: p.box_name,
                total: p.amount + p.commission,
                // Fall back to the flat key so a backend that has not shipped
                // `manual_payment` yet still offers the phone number.
                info: p.manual_payment ?? { phone: p.manager_phone },
            })
        })
    return Array.from(byBox.values())
}

export interface BucketSummary {
    bucket: PayableBucket
    items: Payable[]
    count: number
    /** amount + commission, i.e. what the member is actually charged */
    total: number
    selectedCount: number
    allSelected: boolean
}

export const summarise = (
    payables: Payable[],
    bucket: PayableBucket
): BucketSummary => {
    const items = payables.filter((p) => p.bucket === bucket)
    const selected = items.filter((p) => p.isSelected)
    return {
        bucket,
        items,
        count: items.length,
        total: items.reduce((sum, p) => sum + p.amount + p.commission, 0),
        selectedCount: selected.length,
        allSelected: items.length > 0 && selected.length === items.length,
    }
}

/** Totals for the sticky footer — selection-aware, record items excluded. */
export const selectedTotals = (payables: Payable[]) => {
    const selected = payables.filter((p) => p.isSelected && p.bucket !== 'record')
    return {
        count: selected.length,
        amount: selected.reduce((sum, p) => sum + p.amount, 0),
        commission: selected.reduce((sum, p) => sum + p.commission, 0),
    }
}

/**
 * What the home card shows: two lines, matching the two CTAs.
 *
 * Deliberately NOT the same split as the pay screen. On home, everything that
 * is not a late installment is one line, because the card's job is "is
 * anything on fire, and how much do I owe in total" — not a taxonomy.
 */
export const homeSummary = (payables: Payable[]) => {
    const overdue = summarise(payables, 'overdue')
    const soon = (['dues', 'upcoming', 'cashbox'] as PayableBucket[]).map((b) =>
        summarise(payables, b)
    )
    const [dues, upcoming, cashbox] = soon
    const record = summarise(payables, 'record')
    const soonCount = soon.reduce((n, s) => n + s.count, 0)
    const soonTotal = soon.reduce((n, s) => n + s.total, 0)
    return {
        overdueCount: overdue.count,
        overdueTotal: overdue.total,
        soonCount,
        soonTotal,
        // Name the row after what is actually in it. Calling a list of five
        // membership dues «حق عضویت و پرداختنی‌های پیش‌رو» invents a category
        // the user cannot see.
        soonLabel:
            upcoming.count + cashbox.count === 0
                ? 'حق عضویت'
                : dues.count === 0
                ? 'پرداختنی‌های پیش‌رو'
                : 'حق عضویت و پرداختنی‌های پیش‌رو',
        recordCount: record.count,
        grandTotal: overdue.total + soonTotal,
        hasAnything: overdue.count + soonCount > 0,
        /** worst lateness across overdue installments, for the red badge */
        worstDaysOverdue: overdue.items.reduce(
            (worst, p) => Math.max(worst, p.days_overdue ?? 0),
            0
        ),
    }
}
